import { useMemo } from 'react';
import type { PublicPetitionWizardDraft } from '../../../types/publicPetitionCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import WizardMultiSelectChips from '../../../components/ui/WizardMultiSelectChips';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useCaseRequestWizard } from '../../../hooks/useCaseRequestWizard';
import { FieldError } from './publicPetitionWizardShell';
import { useCaseCategorization } from './useCaseCategorization';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { subFileCategoryRequiredForCategory } from '../../../utils/subFileCategoryRules';

type Props = {
  draft: PublicPetitionWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<PublicPetitionWizardDraft>>;
  editable?: boolean;
  errors?: RequiredErrors;
  onClearError?: (key: string) => void;
};

function SelectField({
  id,
  label,
  required,
  value,
  disabled,
  hasError,
  placeholder,
  options,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  disabled?: boolean;
  hasError?: boolean;
  placeholder: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
  onClear?: () => void;
}) {
  return (
    <div className="ccr-fg">
      <label>
        {label} {required ? <span className="req">*</span> : null}
      </label>
      <WizardSelect
        id={id}
        value={value}
        disabled={disabled}
        hasError={hasError}
        clearable={Boolean(onClear) && !disabled}
        placeholder={placeholder}
        options={options.map((o) => ({ id: o.id, label: o.name }))}
        onChange={onChange}
        onClear={onClear}
      />
    </div>
  );
}

export function PublicPetitionCaseCategorizationCard({
  draft,
  setDraft,
  editable = false,
  errors = {},
  onClearError,
}: Props) {
  const f = useWizardFieldLabels();
  const { switchFileCategory } = useCaseRequestWizard();
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId, {
    enabled: editable || Boolean(draft.fileCategoryId),
  });

  const fileCategoryOptions = useMemo(() => {
    const byId = new Map(cat.fileCategories.map((c) => [c.id, c]));
    if (draft.fileCategoryId && !byId.has(draft.fileCategoryId)) {
      byId.set(draft.fileCategoryId, {
        id: draft.fileCategoryId,
        name: cat.fileCategoryName || draft.fileCategory || draft.fileCategoryId,
      });
    }
    return Array.from(byId.values());
  }, [cat.fileCategories, cat.fileCategoryName, draft.fileCategory, draft.fileCategoryId]);

  const subCategoryOptions = useMemo(() => {
    const byId = new Map(cat.subCategories.map((s) => [s.id, s]));
    if (draft.subFileCategoryId && !byId.has(draft.subFileCategoryId)) {
      byId.set(draft.subFileCategoryId, {
        id: draft.subFileCategoryId,
        name: cat.subFileCategoryName || draft.subFileCategoryName || draft.subFileCategoryId,
      });
    }
    return Array.from(byId.values());
  }, [cat.subCategories, cat.subFileCategoryName, draft.subFileCategoryId, draft.subFileCategoryName]);

  const applicableAreaOptions = useMemo(() => {
    const byId = new Map(cat.applicableAreaOptions.map((o) => [o.id, o]));
    draft.applicableAreaIds.forEach((id, index) => {
      if (!id || byId.has(id)) return;
      const label = draft.applicableAreas[index]?.trim() || id;
      byId.set(id, { id, label });
    });
    return Array.from(byId.values());
  }, [cat.applicableAreaOptions, draft.applicableAreaIds, draft.applicableAreas]);

  const onFileCategoryChange = (id: string) => {
    if (!editable || !setDraft) return;
    const row = cat.fileCategories.find((c) => c.id === id);
    const name = row?.name ?? '';
    switchFileCategory({
      fromFlow: 'public-petition',
      categoryId: id,
      categoryName: name,
      categoryCode: row?.code,
    });
    onClearError?.('fileCategoryId');
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
  };

  const onSubCategoryChange = (id: string) => {
    if (!editable || !setDraft || !draft.fileCategoryId) return;
    const name = cat.subCategories.find((s) => s.id === id)?.name ?? '';
    setDraft((d) => ({
      ...d,
      subFileCategoryId: id,
      subFileCategoryName: name,
      applicableAreaIds: [],
      applicableAreas: [],
      documents: [],
    }));
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
  };

  const onAreasChange = (ids: string[]) => {
    if (!editable || !setDraft) return;
    setDraft((d) => ({
      ...d,
      applicableAreaIds: ids,
      applicableAreas: cat.resolveAreaLabels(ids),
    }));
    onClearError?.('applicableAreas');
  };

  const fileCategorySelected =
    Boolean(draft.fileCategoryId) &&
    !cat.loadingCategories &&
    cat.fileCategories.some((c) => c.id === draft.fileCategoryId);

  const subCategorySelected =
    Boolean(draft.subFileCategoryId) &&
    !cat.loadingSubCategories &&
    cat.subCategories.some((s) => s.id === draft.subFileCategoryId);

  const selectedFileCategory = cat.fileCategories.find((c) => c.id === draft.fileCategoryId);
  const subFileCategoryRequired = subFileCategoryRequiredForCategory(
    selectedFileCategory ?? { id: draft.fileCategoryId, name: draft.fileCategory },
    cat.subCategories,
    cat.loadingSubCategories,
  );
  const subDisabled = !fileCategorySelected || cat.loadingSubCategories;
  const areasDisabled =
    !fileCategorySelected || cat.loadingAreas || (cat.subCategories.length > 0 && !subCategorySelected);

  return (
    <div className={`ccr-cat-card${editable ? '' : ' ccr-cat-card--summary'}`}>
      <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
        <div>
          <SelectField
            id="pp-fileCategory"
            label={f.fileCategory}
            required
            value={draft.fileCategoryId}
            disabled={!editable || cat.loadingCategories}
            hasError={editable && Boolean(errors.fileCategoryId)}
            placeholder={f.selectFileCategory}
            options={fileCategoryOptions}
            onChange={onFileCategoryChange}
            onClear={editable ? () => onFileCategoryChange('') : undefined}
          />
          {editable ? <FieldError message={errors.fileCategoryId} /> : null}
        </div>
        <div>
          <SelectField
            id="pp-subFileCategory"
            label={f.subFileCategory}
            required={subFileCategoryRequired}
            value={draft.subFileCategoryId}
            disabled={!editable || subDisabled}
            hasError={editable && Boolean(errors.subFileCategoryId)}
            placeholder={f.selectSubFileCategory}
            options={subCategoryOptions}
            onChange={onSubCategoryChange}
            onClear={editable ? () => onSubCategoryChange('') : undefined}
          />
          {editable ? <FieldError message={errors.subFileCategoryId} /> : null}
        </div>
      </div>

      <div className="ccr-fg" id="pp-applicableAreas" style={{ marginBottom: 0 }}>
        <label>{f.applicableAreas}</label>
        <WizardMultiSelectChips
          id="pp-applicableAreas-input"
          options={applicableAreaOptions}
          value={draft.applicableAreaIds}
          onChange={onAreasChange}
          disabled={!editable || areasDisabled}
          hasError={editable && Boolean(errors.applicableAreas)}
          placeholder={f.selectApplicableAreas}
          aria-label={f.applicableAreas}
        />
        {editable ? <FieldError message={errors.applicableAreas} /> : null}
      </div>
    </div>
  );
}
